import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Star, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import { ReviewPhotoUpload } from "../components/ImageUpload";

const Review = () => {
  const { token } = useParams();
  const [orderInfo, setOrderInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/review/${token}`)
      .then((r) => setOrderInfo(r.data))
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        setLoadError(typeof detail === "string" ? detail : "Reviewlink kon niet worden geladen.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      toast.error("Geef minstens 1 ster.");
      return;
    }
    if (body.trim().length < 10) {
      toast.error("Vertel iets meer (minimaal 10 tekens).");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/review/${token}`, {
        rating,
        title: title.trim() || null,
        body: body.trim(),
        display_name: displayName.trim() || null,
      });
      setSubmitted(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Verzenden mislukt.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="review-page" className="min-h-screen relative">
      <section className="py-16 sm:py-24 border-b border-[var(--tha-border)]">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <div className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
            REVIEW · DEEL JE TIK
          </div>
          <h1 className="font-display text-5xl sm:text-7xl mt-4 leading-none">
            HOE <span className="neon-red-text">SMAAKTE</span> HET?
          </h1>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          {loading ? (
            <div className="text-[var(--tha-muted)] font-mono-x">Laden…</div>
          ) : loadError ? (
            <div
              data-testid="review-error"
              className="border border-[var(--tha-red)] bg-[#1a0000] p-6 flex items-start gap-3"
            >
              <AlertTriangle className="text-[var(--tha-red)] shrink-0 mt-0.5" size={18} />
              <div>
                <div className="font-display text-2xl">OEPS.</div>
                <p className="mt-2 text-sm text-[var(--tha-muted)]">{loadError}</p>
                <Link to="/shop" className="inline-block mt-4 text-sm text-[var(--tha-red)] underline">
                  → Terug naar shop
                </Link>
              </div>
            </div>
          ) : submitted ? (
            <div
              data-testid="review-submitted"
              className="border border-[var(--tha-border)] bg-[#0a0a0a] p-8 space-y-6"
            >
              <div>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-[var(--tha-red)] bg-[#1a0000] mb-5">
                  <Check className="text-[var(--tha-red)]" size={22} />
                </div>
                <div className="font-display text-4xl">BEDANKT!</div>
                <p className="mt-3 text-[var(--tha-muted)] max-w-lg">
                  We hebben je review ontvangen. Hij verschijnt zodra we hem hebben gekeurd
                  (meestal binnen 24 uur). Dikke proost!
                </p>
              </div>

              <div className="pt-6 border-t border-[var(--tha-border)]">
                <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-red)] mb-2">
                  EXTRA · DEEL JE FOTO'S
                </div>
                <div className="font-display text-2xl mb-1">
                  HAD JIJ EEN GOEDE AVOND?
                </div>
                <p className="text-sm text-[var(--tha-muted)] mb-4">
                  Voeg maximaal 3 foto's toe (van jou, je vrienden, je glas — alles mag).
                  Ze verschijnen pas na goedkeuring.
                </p>
                <ReviewPhotoUpload
                  token={token}
                  photos={photos}
                  onChange={setPhotos}
                />
              </div>

              <div className="pt-4">
                <Link
                  to="/shop"
                  className="inline-block px-5 py-3 font-display tracking-wider text-sm bg-[var(--tha-red)] text-black hover:opacity-90"
                  data-testid="review-back-to-shop"
                >
                  TERUG NAAR SHOP
                </Link>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="review-form"
              className="border border-[var(--tha-border)] bg-[#0a0a0a] p-6 sm:p-8 space-y-6"
            >
              <div>
                <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-2">
                  HEY {(orderInfo?.first_name || "JIJ").toUpperCase()} — JE BESTELDE
                </div>
                <div className="flex flex-wrap gap-3">
                  {(orderInfo?.products || []).map((p) => (
                    <div
                      key={p.id}
                      className="inline-flex items-center gap-2 border border-[var(--tha-border)] px-3 py-2 text-xs font-mono-x"
                      style={{ borderColor: `${p.accent_color}66` }}
                    >
                      <img src={p.image} alt="" className="w-8 h-8 object-cover" />
                      <span className="text-white">{p.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-2">
                  HOEVEEL STERREN? *
                </label>
                <div
                  className="inline-flex gap-1"
                  onMouseLeave={() => setHoverRating(0)}
                  data-testid="review-stars"
                >
                  {[1, 2, 3, 4, 5].map((n) => {
                    const active = (hoverRating || rating) >= n;
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRating(n)}
                        onMouseEnter={() => setHoverRating(n)}
                        className="p-1 transition-transform hover:scale-110"
                        data-testid={`review-star-${n}`}
                        aria-label={`${n} sterren`}
                      >
                        <Star
                          size={32}
                          strokeWidth={1.5}
                          fill={active ? "#ff1e1e" : "transparent"}
                          color={active ? "#ff1e1e" : "#3f3f3f"}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label
                  htmlFor="review-title"
                  className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-1"
                >
                  TITEL (OPTIONEEL)
                </label>
                <input
                  id="review-title"
                  data-testid="review-title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={120}
                  placeholder='Bijv. "Genadeloos lekker"'
                  className="w-full bg-[#050505] border border-[var(--tha-border)] px-4 py-3 text-sm focus:outline-none focus:border-[var(--tha-red)]"
                />
              </div>

              <div>
                <label
                  htmlFor="review-body"
                  className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-1"
                >
                  JE REVIEW *
                </label>
                <textarea
                  id="review-body"
                  data-testid="review-body-input"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={5}
                  maxLength={2000}
                  required
                  placeholder="Eerlijk, kort, brutaal. Alles mag."
                  className="w-full bg-[#050505] border border-[var(--tha-border)] px-4 py-3 text-sm focus:outline-none focus:border-[var(--tha-red)]"
                />
                <div className="mt-1 text-[10px] font-mono-x text-[var(--tha-muted)] text-right">
                  {body.length} / 2000
                </div>
              </div>

              <div>
                <label
                  htmlFor="review-name"
                  className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-1"
                >
                  WEERGEEFNAAM (OPTIONEEL)
                </label>
                <input
                  id="review-name"
                  data-testid="review-name-input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={60}
                  placeholder={orderInfo?.first_name ? `${orderInfo.first_name} (standaard)` : "Standaard: voornaam"}
                  className="w-full bg-[#050505] border border-[var(--tha-border)] px-4 py-3 text-sm focus:outline-none focus:border-[var(--tha-red)]"
                />
                <div className="mt-1 text-xs font-mono-x text-[var(--tha-muted)]">
                  Leeg laten? Dan tonen we automatisch je voornaam + achterletter.
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="review-submit"
                className="w-full sm:w-auto bg-[var(--tha-red)] text-black font-display tracking-wider px-6 py-4 hover:opacity-90 disabled:opacity-40"
              >
                {submitting ? "VERZENDEN…" : "VERZEND REVIEW"}
              </button>
              <p className="text-xs font-mono-x text-[var(--tha-muted)]">
                Je e-mailadres wordt nooit getoond. We modereren reviews voor we ze publiek maken.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Review;
